/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.session.UpdateSessionRequest
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_fee_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_load_patients
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_save_failed

class EditSessionViewModel(
    private val sessionId: String,
    private val sessionRepository: SessionRepository,
    private val patientRepository: PatientRepository,
    private val currencyFormatter: CurrencyFormatter,
    val dateFormatter: DateFormatter,
) : BaseViewModel<LogSessionState, LogSessionEvent, LogSessionAction>(LogSessionState()) {

    init {
        loadPatients()
        loadSession()
    }

    override fun handleAction(action: LogSessionAction) {
        when (action) {
            is LogSessionAction.PatientSelected -> {
                mutableStateFlow.value = state.copy(
                    selectedPatientId = action.patientId,
                    error = null,
                )
            }
            is LogSessionAction.DateChanged ->
                mutableStateFlow.value = state.copy(sessionDate = action.date, error = null)
            is LogSessionAction.FeeChanged ->
                mutableStateFlow.value = state.copy(feeInput = action.fee, error = null)
            is LogSessionAction.TypeChanged ->
                mutableStateFlow.value = state.copy(sessionType = action.type, error = null)
            LogSessionAction.SaveClicked -> handleSave()
        }
    }

    private fun loadPatients() {
        viewModelScope.launch {
            patientRepository.getPatients()
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingPatients = false,
                        error = e.toUiText(Res.string.sessions_error_load_patients),
                    )
                }
                .collect { patients ->
                    mutableStateFlow.value = state.copy(patients = patients, isLoadingPatients = false)
                }
        }
    }

    private fun loadSession() {
        viewModelScope.launch {
            sessionRepository.getSession(sessionId)
                .onSuccess { session ->
                    mutableStateFlow.value = state.copy(
                        selectedPatientId = session.patientId,
                        sessionDate = LocalDate.parse(session.sessionDate),
                        feeInput = (session.fee / 100).toString(),
                        sessionType = session.sessionType.name,
                    )
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        error = e.toUiText(Res.string.sessions_error_save_failed),
                    )
                }
        }
    }

    private fun handleSave() {
        val feeCentavos = currencyFormatter.parse(state.feeInput)

        if (feeCentavos == null || feeCentavos <= 0) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.sessions_error_fee_invalid))
            return
        }

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            sessionRepository.updateSession(
                sessionId = sessionId,
                request = UpdateSessionRequest(
                    sessionDate = state.sessionDate.toString(),
                    fee = feeCentavos,
                    sessionType = state.sessionType,
                ),
            )
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(LogSessionEvent.SaveSuccess)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isSaving = false,
                        error = e.toUiText(Res.string.sessions_error_save_failed),
                    )
                }
        }
    }
}
